import React, { type FC, useState } from 'react';

import { cx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import PlaylistAddIcon from '@atlaskit/icon-lab/core/playlist-add';
import PlaylistAddedIcon from '@atlaskit/icon-lab/core/playlist-added';
import PlaylistRemoveIcon from '@atlaskit/icon-lab/core/playlist-remove';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

export interface testId {
	testId?: string;
}

interface addToPlaylist {
	addToPlaylist: () => void;
}

interface removeFromPlaylist {
	removeFromPlaylist: () => void;
}

export interface ModifyPlaylistButtonProps extends testId, addToPlaylist, removeFromPlaylist {
	_defaultEngagement_do_not_use?: boolean;
	isInPlaylist: boolean;
	round?: boolean;
}

const messages = defineMessages({
	addToPlaylistText: {
		id: 'ai-audio.playlist.button.addToPlaylist-label',
		defaultMessage: 'Add to playlist',
		description: 'Label for the chevron up icon to collapse the playlist',
	},
	removeFromPlaylistText: {
		id: 'ai-audio.playlist.button.removeFromPlaylist-label',
		defaultMessage: 'Remove from playlist',
		description: 'Label for the chevron down icon to expand the playlist',
	},
	inPlaylistText: {
		id: 'ai-audio.playlist.button.inPlaylist-label',
		defaultMessage: 'Added to playlist',
		description: 'Label for the chevron down icon to expand the playlist',
	},
} as const);

const styles = cssMap({
	container: {
		//typecheck refuses to allow space.400 tokens into height or width
		height: '32px',
		width: '32px',
	},
	round: {
		borderRadius: token('radius.full'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		overflow: 'clip',
	},
});

export const AddToPlaylistButton: FC<testId & addToPlaylist> = ({ testId, addToPlaylist }) => {
	const { formatMessage } = useIntl();

	return (
		<Tooltip content={formatMessage(messages.addToPlaylistText)}>
			{(tooltipProps) => (
				<IconButton
					appearance="subtle"
					icon={PlaylistAddIcon}
					testId={testId && `${testId}-add`}
					label={formatMessage(messages.addToPlaylistText)}
					{...tooltipProps} //beware: if this is after onCLick, it stops working
					onClick={addToPlaylist}
				/>
			)}
		</Tooltip>
	);
};

export const RemoveFromPlaylistButton: FC<testId & removeFromPlaylist> = ({
	testId,
	removeFromPlaylist,
}) => {
	const { formatMessage } = useIntl();

	return (
		<Tooltip content={formatMessage(messages.removeFromPlaylistText)}>
			{(tooltipProps) => (
				<IconButton
					appearance="subtle"
					icon={PlaylistRemoveIcon}
					testId={testId && `${testId}-remove`}
					label={formatMessage(messages.removeFromPlaylistText)}
					{...tooltipProps} //beware: if this is after onCLick, it stops working
					onClick={removeFromPlaylist}
				/>
			)}
		</Tooltip>
	);
};

//note: when using ModifyPlaylistButton abstraction the tooltip will not be seen because it changes to remove button
export const InPlaylistButton: FC<testId & removeFromPlaylist> = ({
	testId,
	removeFromPlaylist,
}) => {
	const { formatMessage } = useIntl();

	return (
		<Tooltip content={formatMessage(messages.inPlaylistText)}>
			{(tooltipProps) => (
				<IconButton
					appearance="subtle"
					icon={PlaylistAddedIcon}
					testId={testId && `${testId}-added`}
					label={formatMessage(messages.inPlaylistText)}
					{...tooltipProps} //beware: if this is after onCLick, it stops working
					onClick={removeFromPlaylist}
				/>
			)}
		</Tooltip>
	);
};

export const ModifyPlaylistButton: FC<ModifyPlaylistButtonProps> = ({
	testId,
	isInPlaylist,
	addToPlaylist,
	removeFromPlaylist,
	round,
	_defaultEngagement_do_not_use = false,
}) => {
	const [isEnagaged, setIsEngaged] = useState(_defaultEngagement_do_not_use);

	return (
		<Box
			// Set the state to true when the mouse enters or the element receives focus
			onMouseEnter={() => setIsEngaged(true)}
			onFocus={() => setIsEngaged(true)}
			// Set the state back to false when the mouse leaves or the element loses focus
			onMouseLeave={() => setIsEngaged(false)}
			onBlur={() => setIsEngaged(false)}
			xcss={cx(styles.container, round && styles.round)}
		>
			{isInPlaylist ? (
				!isEnagaged ? (
					<InPlaylistButton testId={testId} removeFromPlaylist={removeFromPlaylist} />
				) : (
					<RemoveFromPlaylistButton testId={testId} removeFromPlaylist={removeFromPlaylist} />
				)
			) : (
				<AddToPlaylistButton testId={testId} addToPlaylist={addToPlaylist} />
			)}
		</Box>
	);
};
